import React, { useState, useReducer, useContext, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Form, Container } from "react-bootstrap";
import { validateInvoiceForm } from "../validationFunctions/inputValidations.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FormErrorAction,
  FormErrorState,
  InvoiceFormErrorState,
  PaymentError,
} from "types/validationError.types.ts";
import { v4 as uuidv4 } from 'uuid';

import paymentOptions from '../mockdata/paymentOptions.json';
import { UUIDContext, PBLResponseContext } from "../UUIDContexted";
import FormComponent from "./FormComponent";
import { submitInvoicePaymentRequest } from "../api/apiService.ts";
import CONSTANTS from "../utils/constants";
import fetchIpAddress from "../utils/fetchIP.js";
import { PaymentProcessingLoader } from './PaymentProcessingLoader.tsx';
import PaylaDcs from '../components/PaylaDsc.tsx';
import { PaymentFormErrors, clearEmptyErrors, usePaymentForm } from "../context/PaymentFormContext";
import '../components/invoice.css';
import PaymentNotSupported from "./PaymentNotSupported.tsx";

//custom error message component
const FormFieldError: React.FC<{
  id: string;
  value: string;
  testId: string;
}> = ({ id, value, testId }) =>
    value !== "typing" &&
    value !== "" && (
      <span
        id={id}
        data-testid={testId}
        className="error_message"
      >
        <FormattedMessage id={value} />
      </span>
    );

const Invoice: React.FC = () => {
  const PBLData = useContext(UUIDContext);
  const { setPblResponseData } = useContext(PBLResponseContext);
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
  const [snippetToken, setSnippetToken] = useState("");
  const intl = useIntl();
  const { PaymentFormData, setPaymentFormData, PaymentFormErrors, setPaymentFormErrors } = usePaymentForm();
  const [overviewAttempted, setOverviewAttempted] = useState(false);
  const [, setIsOverviewPage] = useState(false);
  const paymentErrorRef = useRef<PaymentError>({ iban: "", bankAccountHolderError: "" });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    formattedDate: "",
    phone: "",
    email: "",
    agreed: false,
    selectedBusiness: "Business to Customer",
    companyName: "",
    address: "",
    zipcode: "",
    city: "",
    selectedCountry: "Germany",
    retainedBusinessValue: "",
    paymentMethod: "3390",
    linkId: PBLData?.linkId,
    // isOverviewPage: false,
  });

  const [fieldVisibility, setFieldVisibility] = useState({
    isCity: false,
    isStreet: false,
    isZip: false,
    isCountryCode: false,
    isBussiness: false,
    isCompanyName: false,
    isFirstName: false,
    isLastName: false,
    isDob: false,
    isEmail: false,
    isPhone: false,
    isLabelForCustomer: false,
    isLabelForContact: false,
    isLabelForBilling: false,
  });

  //generate snippetToken only once
  useEffect(() => {
    const partnerMerchantId = PBLData ? PBLData.bnplId : '';
    setSnippetToken(`${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`);
    setPblResponseData(null);
  }, []);

  useEffect(() => {
    if (PBLData && PBLData.requiredParameters) {
      const filterPaymentMethod = PBLData?.requiredParameters.find(item => item['3390']);
      const dataOfInvoiceMethod = filterPaymentMethod ? filterPaymentMethod['3390'] : null;

      const updatedfieldVisibility = { ...fieldVisibility };

      dataOfInvoiceMethod?.forEach((field) => {
        if (field === "customer.billingAddress.city") {
          updatedfieldVisibility.isCity = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.billingAddress.street") {
          // Billing Address
          updatedfieldVisibility.isStreet = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.billingAddress.zip") {
          updatedfieldVisibility.isZip = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.billingAddress.countryCode") {
          updatedfieldVisibility.isCountryCode = true;
          updatedfieldVisibility.isLabelForBilling = true;
        } else if (field === "customer.personalInformation.name.firstName") {
          // Personal Information Fields
          updatedfieldVisibility.isFirstName = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        } else if (field === "customer.personalInformation.name.surname") {
          updatedfieldVisibility.isLastName = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        } else if (field === "customer.personalInformation.dateOfBirth") {
          updatedfieldVisibility.isDob = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        } else if (field === "customer.contactDetails.emailAddress") {
          // Contact Details
          updatedfieldVisibility.isEmail = true;
          updatedfieldVisibility.isLabelForContact = true;
        } else if (field === "customer.contactDetails.phoneNumber") {
          updatedfieldVisibility.isPhone = true;
          updatedfieldVisibility.isLabelForContact = true;
        } else if (field === "customer.businessRelation") {
          updatedfieldVisibility.isBussiness = true;
          updatedfieldVisibility.isCompanyName = true;
          updatedfieldVisibility.isLabelForCustomer = true;
        }
      });
      setFieldVisibility(updatedfieldVisibility);
    }
    if (PBLData && PBLData.paymentMethods && PBLData.paymentMethods.length > 0) {
      let invoicePaymentMethodsID = paymentOptions.find(option => option.id === "invoice")?.paymentMethodsID;
      if (invoicePaymentMethodsID) {
        let isPaymentMethodAvailable = PBLData.paymentMethods.includes(invoicePaymentMethodsID);
        setIsPaymentMethodAvailable(isPaymentMethodAvailable)
      }
    }
  }, [PBLData]);

  //this function recurses through formErrorState and search for non-empty strings (errors) and returns true if errors not present
  const containsError = (errors: InvoiceFormErrorState): boolean => {
    const check = (obj: any): boolean => {
      for (const key in obj) {
        if (typeof obj[key] === "object") {
          if (check(obj[key])) {
            return true;
          }
        } else if (typeof obj[key] === "string" && obj[key] !== "") {
          return true;
        }
      }
      return false;
    };
    return check(errors);
  };

  const handleSelect = (eventKey: any) => {
    setFormData((prevState) => ({
      ...prevState,
      selectedBusiness: eventKey,
    }));

    if (eventKey === "Business to Customer" && formData.companyName) {
      setFormData((prevState) => ({
        ...prevState,
        retainedBusinessValue: formData.companyName,
      }));
      setFormData((prevState) => ({
        ...prevState,
        companyName: "",
      }));
    } else if (eventKey === "Business to Business") {
      setFieldVisibility((prevState) => ({
        ...prevState,
        isCompanyName: true,
      }));
      setFormData((prevState) => ({
        ...prevState,
        companyName: formData.retainedBusinessValue,
      }));
    }
  };

  const handleSelectCountry = (eventKey: string | null) => {
    if (eventKey) {
      setFormData((prevState) => ({
        ...prevState,
        selectedCountry: eventKey,
      }));

      setPaymentFormData({
        ...PaymentFormData,
        selectedCountry: eventKey,
      });
    }
  };

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value.replace(/\D/g, '');

    if (value.length > 10) {
      return;
    }

    // Code to format DOB 
    let formatted = '';
    if (value.length <= 2) {
      formatted = value;
    } else if (value.length <= 4) {
      formatted = `${value.slice(0, 2)}.${value.slice(2)}`;
    } else {
      formatted = `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)}`;
    }

    setFormData((prevState) => ({
      ...prevState,
      dob: formatted,
    }));

    setPaymentFormData({
      ...PaymentFormData,
      dob: formatted,
    });

    const datePattern = /^\d{10}$/;

    if (datePattern.test(value)) {
      const [day, month, year] = [
        value.slice(0, 2),
        value.slice(2, 4),
        value.slice(4, 8),
      ];
      const date = new Date(`${year}-${month}-${day}`);
      if (
        date.getFullYear() === +year &&
        date.getMonth() + 1 === +month &&
        date.getDate() === +day
      ) {
        setFormData((prevState) => ({
          ...prevState,
          formattedDate: value,
        }));
      } else {
        setFormData((prevState) => ({
          ...prevState,
          formattedDate: "",
        }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        formattedDate: "",
      }));
    }
  };

  const updateFormData = (key: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));

    setPaymentFormData({
      ...PaymentFormData,
      [key]: value,
    });
  };

  const initialErrorState: InvoiceFormErrorState = {
    customerError: {
      companyNameError: "",
      firstNameError: "",
      lastNameError: "",
      dobError: "",
    },
    contactError: {
      emailError: "",
      phoneError: "",
    },
    billingError: {
      addressError: "",
      zipcodeError: "",
      cityError: "",
      countryError: ""
    },
    notAgreedError: ""
  };

  const formErrorReducer = (
    errorState: InvoiceFormErrorState,
    action: FormErrorAction
  ): InvoiceFormErrorState => {
    switch (action.type) {
      case "SET_CUSTOMER_ERROR":
        return {
          ...errorState,
          customerError: {
            ...errorState.customerError,
            [action.field]: action.value,
          },
        };
      case "SET_CONTACT_ERROR":
        return {
          ...errorState,
          contactError: {
            ...errorState.contactError,
            [action.field]: action.value,
          },
        };
      case "SET_BILLING_ERROR":
        return {
          ...errorState,
          billingError: {
            ...errorState.billingError,
            [action.field]: action.value,
          },
        };
      case "SET_NOT_AGREED_ERROR":
        return {
          ...errorState,
          notAgreedError: action.value,
        };
      case "SET_ALL_ERROR":
        return {
          ...action.errors,
          notAgreedError: "",
        } as InvoiceFormErrorState;
      default:
        return errorState;
    }
  };

  const [formErrorState, errorDispatch] = useReducer(
    formErrorReducer,
    initialErrorState
  );

  const [userInputState, setUserInputState] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    dob: '',
    phone: '',
    email: '',
    address: '',
    zip: '',
    city: '',
    iban: '',
    accountHolder: '',
  });

  const handleSubmit = async () => {
    validateInvoiceForm(
      formData,
      fieldVisibility,
      errorDispatch,
      false
    );
    setSubmissionAttempted(true);
  }

  const handleOverviewPage = async () => {
    validateInvoiceForm(
      formData,
      fieldVisibility,
      errorDispatch,
      true
    );
    setOverviewAttempted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    if (overviewAttempted) {
      const hasError = containsError(formErrorState);
      if (!hasError) {
        setIsOverviewPage(true);
        const updatedFormData = {
          ...formData,
          selectedBusiness: formData.selectedBusiness ? formData.selectedBusiness : "Business to Customer", // Default value
          selectedCountry: formData.selectedCountry ? formData.selectedCountry : 'Germany', // Default valueconte
          paymentMethod: '3390',
          isOverviewPage: true,
        };
        setFormData(updatedFormData);
        setPaymentFormData(updatedFormData);
      }
      setOverviewAttempted(false);
    }
  }, [formErrorState, overviewAttempted]);

  function convertDateFormat(dateStr: string): string {
    const parts = dateStr.split('.');

    if (parts && parts.length > 2) {
      const [dd, mm, yyyy] = parts;
      return `${yyyy}${mm}${dd}`;
    }
    return '';
  }

  useEffect(() => {
    const makePostRequest = async () => {
      if (submissionAttempted) {
        const hasError = containsError(formErrorState);
        if (!hasError) {
          setShowPaymentProcessing(true);
          setPblResponseData(null);
          // code for the post request
          try {
            const updatedFormData = {
              ...formData,
              companyName: formData.companyName.trim(),
              firstName: formData.firstName.trim(),
              lastName: formData.lastName.trim(),
              dob: convertDateFormat(formData.dob),
              phone: formData.phone.replace(/\s/g, ''),
              address: formData.address.trim(),
              zipcode: formData.zipcode.trim(),
              city: formData.city.trim(),
              selectedBusiness: fieldVisibility.isBussiness
                ? (formData.selectedBusiness == 'Business to Customer' ? 'B2C' : 'B2B')
                : "",
              selectedCountry: fieldVisibility.isCountryCode
                ? formData.selectedCountry
                : "",
            };
            const ipAddress = await fetchIpAddress();
            const response = await submitInvoicePaymentRequest(updatedFormData, ipAddress, snippetToken);
            const paymentStatus = response.createPaymentResponse.payment.status;
            if (
              paymentStatus === "PENDING_CAPTURE" ||
              paymentStatus === "CREATED" || paymentStatus === "CAPTURED"
            ) {
              setPblResponseData({ ...response.createPaymentResponse, linkId: uuid });
              navigate(`/${uuid}/success/`);
            } else {
              setPblResponseData({ ...response, linkId: uuid });
              setShowPaymentProcessing(false);
              // navigate(`/${uuid}/error/`);
            }
          } catch (error: any) {
            setPblResponseData({ error: error, linkId: uuid });
            setShowPaymentProcessing(false);
            // navigate(`/${uuid}/error/`);
          }
        }
      }
    }
    makePostRequest();

    return () => {
      setSubmissionAttempted(false);
    }
  }, [submissionAttempted]);

  useEffect(() => {
    if (PaymentFormData) {
      setFormData((prevState) => ({
        ...prevState,
        ...PaymentFormData
      }));
    }
  }, []);

  useEffect(() => {
    let { notAgreedError, ...errors } = formErrorState;
    const paymentError = paymentErrorRef.current;
    errors = {
      ...errors,
      paymentError
    } as PaymentFormErrors;
    errors = clearEmptyErrors(errors);
    
    setPaymentFormErrors(errors);
  }, [formErrorState]);

  useEffect(() => {
    if (PaymentFormData && PaymentFormErrors) {
      const { paymentError, ...remainingErrors } = PaymentFormErrors as FormErrorState;
      paymentErrorRef.current = paymentError;
      errorDispatch({
        type: 'SET_ALL_ERROR',
        errors: remainingErrors
      });
    }
  }, []);

  return (
    <>
      {isPaymentMethodAvailable ?
        <Container fluid className="d-flex flex-column g-0" data-testid="invoice_payment_section">
          <Row className="flex-grow-1">
            <Col>
              <FormComponent
                paymentMethodCode={3390}
                fieldVisibility={fieldVisibility}
                onSelect={handleSelect}
                formData={formData}
                onChange={updateFormData}
                handleDateChange={handleDateChange}
                handleSelectCountry={handleSelectCountry}
                formErrorState={formErrorState}
                dispatch={errorDispatch}
                userInputState={userInputState}
                setUserInputState={setUserInputState}
                isOverviewPage={PaymentFormData.isOverviewPage}
              />
            </Col>
          </Row>

          <Row className="d-flex justify-content-center align-items-end mb-24 mt-2">
            <h5 data-testid="pm-label-terms-payment" className="section-title">
              {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE, defaultMessage: "Terms of Payment" })}
            </h5>
            <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
              <Form.Check.Input
                type="checkbox"
                id="terms-checkbox-input"
                className="terms-checkbox-input m-0"
                checked={formData.agreed}
                onChange={(e) => setFormData((prevState) => ({
                  ...prevState,
                  agreed: e.target.checked,
                }))}
                data-testid="pm-terms-checkbox-input"
              />
              <label htmlFor="terms-checkbox-input" className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
                <FormattedMessage
                  id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
                  defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
                />
              </label>
            </div>
            {formData.agreed ? (
              (formErrorState.notAgreedError = "")
            ) : (
              <FormFieldError
                id="not-agree"
                value={formErrorState.notAgreedError}
                testId="pn-not-agree"
              />
            )}
            <PaylaDcs snippetToken={snippetToken} agreed={formData.agreed} />
            <div className="term_policy_text mt-3"
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_DESCRIPTION,
                  defaultMessage: CONSTANTS.PAYMENT_CHECKBOX_DESCRIPTION,
                }),
              }}
            >
            </div>
          </Row>

          <Row className="d-flex align-items-center">
            {showPaymentProcessing ? (
              <Col>
                <PaymentProcessingLoader />
              </Col>
            ) : (
              <Col className="text-end">
                <button
                  className="btn btn-outline rounded-5 px-3 me-2 btn-invoice-outline"
                  onClick={handleOverviewPage}
                  data-testid="pm-overview-btn"
                >
                  <FormattedMessage
                    id={CONSTANTS.PAYMENT_FORM.BUTTON.GO_TO_OVERVIEW}
                    defaultMessage="Go to"
                  />
                </button>
                <button
                  className="btn btn-primary text-white px-4 rounded-4 volte-medium"
                  onClick={handleSubmit}
                  data-testid="pm-submit-btn"
                >
                  <FormattedMessage
                    id={CONSTANTS.PAYMENT_FORM.BUTTON.SUBMIT_PAYMENT}
                    defaultMessage="Complete payment"
                  />
                </button>
              </Col>
            )}
          </Row>
        </Container>
        : <PaymentNotSupported />
      }
    </>
  );
};

export default React.memo(Invoice);
